import {
  canStartLoadingDetails,
  type DocumentListRow,
  useOrderListState,
} from "~/features/orders/orderList/useOrderListState";
import { useCallback, useEffect, useRef } from "react";
import {
  filterProducts,
  type GetOrderListSearchResults,
  getOrderListSearchResults,
  transformFormData,
} from "~/features/orders/common/DownloadOrderHistoryService/DownloadOrderHistoryService";
import { useSearchSubscription } from "~/features/orders/common/hooks/useSearchSubscription";
import { fetchOrderDetails } from "~/features/orders/orderList/api/fetchOrderDetails/fetchOrderDetails";
import { type ReportType } from "~/features/cart/cart/components/Buttons/DownloadReportButton/api/downloadCartReport";
import { downloadOrderHistoryReport } from "~/features/orders/orderList/api/downloadOrderHistoryReport";

import Spinner from "~/common/components/Spinner/Spinner";
import { DownloadReportButtons } from "~/features/orders/common/components/DownloadReportButtons";
import { IncludesSurchargeDisclaimer } from "~/domains/pricing/components/IncludesSurchargeDisclaimer";

import { OrderListTable } from "~/features/orders/orderList/components/OrderListTable/OrderListTable";
import {
  OrderListSearchBox,
  type OrderListSearchBoxData,
  type OrderListSearchBoxHandle,
} from "~/features/orders/orderList/components/OrderListSearchBox/OrderListSearchBox";
import { SearchBoxVariant } from "~/features/orders/orderList/components/OrderListSearchBox/SearchBoxVariant";
import { useTranslation } from "react-i18next";
import { fetchServiceCases } from "~/features/orders/orderDetails/components/ServiceCases/api";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import type { OrderData } from "~/domains/orders/types/modelsV2";
import { isErrorEntry, isFromDifferentAccOrSalesOrg } from "~/features/orders/common/utils/util";
import { Alert } from "~/common/mui/Alert/Alert";
import { PageElements } from "~/common/components/PageElements";
import { OrderQuoteNoResults } from "~/common/components/NoResultsSection/OrderQuoteNoResults";
import { Stack, styled } from "~/common/mui";
import { OrderListTableV2 } from "../orderListV2/OrderListTableV2";
import { OrderListDesignToggle, useDesignToggle } from "../common/components/NewDesignToggle";
import { useDispatch } from "react-redux";
import { orderListMounted } from "~/features/cart/cart/redux/actions/orderDetailsActions";
import { addBaseProductDataAction } from "~/domains/product/redux/actions";
import { orderHistoryStyles } from "~/features/orders/orderStyles";

const OrderHistoryStyleScope = styled(Stack)(({ theme }) => orderHistoryStyles(theme));

export const OrderListPage = () => {
  const { t } = useTranslation();
  const { isNewDesignEnabled } = useDesignToggle();
  const reduxDispatch = useDispatch();
  const [state, orderListDispatch, actions] = useOrderListState();
  const { loading, error, results: rows } = state;
  const submittedFormData = useRef<GetOrderListSearchResults | null>(null);
  const searchBoxRef = useRef<OrderListSearchBoxHandle>(null);

  const isAllOpen = getIsAllOpen(state.results);
  const webservicesDisabled = window.SCHA.config.disableAllWS;

  useEffect(() => {
    reduxDispatch(orderListMounted());
  }, [reduxDispatch]);

  const search = useSearchSubscription(getOrderListSearchResults, {
    onBegin: () => orderListDispatch(actions.beginLoading()),
    onError: () => orderListDispatch(actions.errorLoading()),
    onNext: (data) => orderListDispatch(actions.addResultPage(data)),
    onComplete: () => orderListDispatch(actions.finishLoading()),
  });

  const onSubmitHandler = useCallback(
    (formData: OrderListSearchBoxData) => {
      const transformedData = transformFormData(formData);
      submittedFormData.current = transformedData;
      search(transformedData);
    },
    [search],
  );

  const loadSingleDetail = useCallback(async (row: DocumentListRow<OrderData>) => {
    const soNumber = row.data.soNumber;
    let accountNumber, salesOrg;
    if (isFromDifferentAccOrSalesOrg(row)) {
      accountNumber = row.data.account?.code;
      salesOrg = row.data.account?.salesOrg;
    }
    try {
      let data;
      let serviceCases = {};
      if (userHasPermission(Permission.CAN_SEE_SERVICE_CASES)) {
        [data, serviceCases] = await Promise.all([
          fetchOrderDetails(soNumber, accountNumber, salesOrg),
          fetchServiceCases(soNumber),
        ]);
      } else {
        data = await fetchOrderDetails(soNumber, accountNumber, salesOrg);
      }
      reduxDispatch(addBaseProductDataAction(data.products.map((entry) => entry.product)));
      data.products = filterProducts(data.products, submittedFormData.current);
      data.serviceCases = serviceCases;
      orderListDispatch(actions.finishLoadingDetails(data));
    } catch {
      orderListDispatch(actions.errorLoadingDetails(soNumber));
    }
  }, []);

  const toggleOpenCb = useCallback(
    (row: DocumentListRow<OrderData>) => {
      orderListDispatch(actions.toggleOpen(row.data.soNumber));
      if (canStartLoadingDetails(row)) {
        loadSingleDetail(row);
      }
    },
    [loadSingleDetail],
  );

  const toggleOpenAllCb = useCallback(() => {
    const newState = !isAllOpen;
    rows.forEach((row) => {
      if (newState && canStartLoadingDetails(row) && !isErrorEntry(row)) {
        loadSingleDetail(row);
      }
    });
    orderListDispatch(actions.toggleAllOpen());
  }, [isAllOpen, loadSingleDetail, rows]);

  const downloadReportCb = (fileType: ReportType) => {
    downloadOrderHistoryReport(rows, fileType);
  };

  const containsSurcharge = rows.some(rowHasSurcharge);

  const disableInteractions = state.loading || state.error || webservicesDisabled;
  const showRows = !error && rows.length > 0;

  return (
    <OrderHistoryStyleScope gap={3}>
      <Stack direction={"row"} justifyContent={"space-between"}>
        <PageElements.Title>
          {t("orderListPage.title")} (<span data-test-id={"orderAmount"}>{rows.length}</span>)
        </PageElements.Title>
        {isNewDesignEnabled && !loading && rows.length > 0 && (
          <DownloadReportButtons
            onDownload={downloadReportCb}
            disabled={disableInteractions}
            testIdPrefix="orderList-searchBox"
            label={t("orderListPage.download.pdf")}
          />
        )}
      </Stack>

      <OrderListSearchBox ref={searchBoxRef} onSubmit={onSubmitHandler} variant={SearchBoxVariant.ORDER}>
        {!isNewDesignEnabled && !loading && rows.length > 0 && (
          <DownloadReportButtons
            onDownload={downloadReportCb}
            disabled={disableInteractions}
            testIdPrefix="orderList-searchBox"
            label={t("orderListPage.download.pdf")}
          />
        )}
      </OrderListSearchBox>

      <OrderListDesignToggle />
      {loading && <Spinner />}
      {showRows &&
        (isNewDesignEnabled ? (
          <OrderListTableV2 rows={rows} toggleOpen={toggleOpenCb} toggleOpenAll={toggleOpenAllCb} />
        ) : (
          <OrderListTable rows={rows} toggleOpen={toggleOpenCb} toggleOpenAll={toggleOpenAllCb} />
        ))}

      {<IncludesSurchargeDisclaimer hasSurcharge={containsSurcharge} />}

      {!webservicesDisabled && !loading && error && <Alert severity="error">{t("orderListPage.error")}</Alert>}

      {!loading && !error && !rows.length && (
        <OrderQuoteNoResults title={t("orderListPage.noResults")} onReset={() => searchBoxRef.current?.reset()} />
      )}
    </OrderHistoryStyleScope>
  );
};

export const rowHasSurcharge = (row: DocumentListRow<OrderData>) => {
  const {
    data: { hasSurcharge },
  } = row;

  return hasSurcharge;
};

export const getIsAllOpen = (rows: DocumentListRow<OrderData>[]) => rows.length > 0 && rows.every((item) => item.open);
